"use client";

import { WIDGET_H3 } from "@/components/tool-kit/styles";
import type { BibtexCleanerStrings } from "@/components/tools/bibtex-cleaner";
import { Badge } from "@/components/tools/reference-tools/badge";
import { t } from "@/lib/i18n/interpolate";
import type {
  Change,
  Issue,
} from "@/lib/tool-kit/reference-tools/bibtex-clean";

/*
 * The BibTeX Cleaner's report: every problem the validator found, then every change
 * the clean-up made, grouped by entry. Every word comes from the widget's strings.
 */

function issueText(issue: Issue, strings: BibtexCleanerStrings): string {
  const detail =
    issue.code === "parse" && issue.detail
      ? ((strings.parseErrors as Record<string, string>)[issue.detail] ??
        issue.detail)
      : (issue.detail ?? "");
  return t(strings.issues[issue.code], { detail, other: issue.other ?? "" });
}

function changeText(change: Change, strings: BibtexCleanerStrings): string {
  return t(strings.changes[change.code], {
    field: change.field ?? "",
    before: change.before ?? "",
    after: change.after ?? "",
  });
}

function groupByKey(changes: readonly Change[]): [string, Change[]][] {
  const groups = new Map<string, Change[]>();
  for (const change of changes) {
    const list = groups.get(change.key) ?? [];
    list.push(change);
    groups.set(change.key, list);
  }
  return [...groups.entries()];
}

export function BibReport({
  issues,
  changes,
  changeCount,
  lineLabel,
  strings,
}: {
  issues: readonly Issue[];
  changes: readonly Change[];
  /** "12 changes", already pluralised for the page's locale. */
  changeCount: string;
  lineLabel: (line: number) => string;
  strings: BibtexCleanerStrings;
}) {
  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.issuesHeading}</h3>
        {issues.length === 0 ? (
          <p className="text-muted-foreground text-sm">{strings.noIssues}</p>
        ) : (
          <ul className="space-y-2">
            {issues.map((issue, index) => (
              <li
                className="flex flex-wrap items-baseline gap-2 rounded-lg border border-border bg-card p-3 text-sm"
                // Issues are reported in file order; position is their identity.
                // biome-ignore lint/suspicious/noArrayIndexKey: positional rows
                key={index}
              >
                <Badge tone={issue.severity === "error" ? "bad" : "warn"}>
                  {issue.severity === "error" ? strings.error : strings.warning}
                </Badge>
                <span className="font-mono text-muted-foreground text-xs">
                  {lineLabel(issue.line)} ·{" "}
                  {issue.key ?? strings.outsideEntries}
                </span>
                <span className="w-full text-foreground">
                  {issueText(issue, strings)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.changesHeading}</h3>
        {changes.length === 0 ? (
          <p className="text-muted-foreground text-sm">{strings.noChanges}</p>
        ) : (
          <details>
            <summary className="cursor-pointer text-muted-foreground text-sm">
              {changeCount}
            </summary>
            <ul className="mt-3 space-y-2">
              {groupByKey(changes).map(([key, list], group) => (
                <li
                  className="rounded-lg border border-border bg-card p-3"
                  // Two entries may share a key (that is one of the problems reported).
                  // biome-ignore lint/suspicious/noArrayIndexKey: positional groups
                  key={group}
                >
                  <p className="font-medium font-mono text-foreground text-sm">
                    {key}
                  </p>
                  <ul className="mt-1 list-disc space-y-1 pl-5 text-muted-foreground text-xs">
                    {list.map((change, index) => (
                      <li
                        className="break-words font-mono"
                        // A change log in order of application.
                        // biome-ignore lint/suspicious/noArrayIndexKey: positional rows
                        key={index}
                      >
                        {changeText(change, strings)}
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </div>
  );
}
